import { brouillonCreation, formaterJour } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ErreurApi, type FicheTache, lire, requeteMoi, requetes } from '../api'
import { LIBELLES_STATUT_PIECE, LIBELLES_TYPE_PIECE } from '../libelles'
import { ApercuDocument, Vignette } from './ApercuPiece'
import { ClasserPiece } from './ClasserPiece'
import { ZoneDepotTache } from './DepotTache'
import { Icone, type NomIcone } from './Icone'
import { entitesPourPiece, RattacherPiece } from './RattacherPiece'
import { confirmer } from './confirmation'

// Pièces d'une tâche, en cartes : vignette, format, statut et ce que la lecture en dit. Une
// carte touchée ouvre l'aperçu intégré, la pièce entière à gauche et ses actions à droite
// (rattacher, classer, transférer par courriel, télécharger, rejeter), les mêmes gestes que
// sur la fiche de la pièce. La décision d'un dossier de mail reste dans « Décision ». Sous les
// cartes, une petite zone pour glisser une pièce de plus (quand la tâche en accepte).

type PieceTache = FicheTache['pieces'][number]
type PieceDossier = NonNullable<FicheTache['dossier']>['pieces'][number]

const fichier = (id: string) => `/api/pieces/${id}/fichier`

const format = (p: PieceTache) =>
  p.typeMime === 'application/pdf'
    ? 'PDF'
    : p.typeMime.includes('xml')
      ? 'XML'
      : p.typeMime.startsWith('image/')
        ? 'IMG'
        : 'FICHIER'

const ton = (statut: PieceTache['statut']) =>
  statut === 'a_verifier' ? 'ton-alerte' : statut === 'rattachee' ? 'ton-ok' : ''

/** Ce que la lecture dit de la pièce : décision attendue, ou motifs de vérification. */
function noteDe(d: PieceDossier | undefined): string | null {
  if (!d) return null
  if (d.aRattacher)
    return d.entite ? `À rattacher, proposée pour ${d.entite}.` : 'À rattacher : entité à choisir.'
  if (d.aRegler) return 'Règlement à décider.'
  if (d.aClasser) return 'Rubrique de classement à choisir.'
  if (d.motifs.length > 0) return d.motifs.join(' ')
  return null
}

export function PiecesTache({
  t,
  ecriture,
  tacheId,
}: {
  t: FicheTache
  ecriture: boolean
  tacheId: string
}) {
  const [vue, setVue] = useState<number | null>(null)
  const pieces = t.pieces
  const dossier = new Map((t.dossier?.pieces ?? []).map((p) => [p.id, p]))
  const ouverte = vue !== null ? pieces[vue] : undefined
  return (
    <div className="pj">
      <div className="pj-tete">
        <Icone nom="attach_file" taille={18} />
        <strong>Pièces jointes</strong>
        <span className="mono discret">{pieces.length}</span>
      </div>
      <div className="pj-grille">
        {pieces.map((p, i) => {
          const note = noteDe(dossier.get(p.id))
          return (
            <div key={p.id} className={vue === i ? 'pj-carte choisie' : 'pj-carte'}>
              <button
                type="button"
                className="pj-vignette"
                title="Aperçu"
                aria-label={`Aperçu de ${p.nomFichier ?? 'la pièce'}`}
                aria-pressed={vue === i}
                onClick={() => setVue(vue === i ? null : i)}
              >
                <Vignette piece={p} />
                <span className="pj-format">{format(p)}</span>
                <span className={`badge pj-statut ${ton(p.statut)}`}>
                  {LIBELLES_STATUT_PIECE[p.statut]}
                </span>
                <span className="pj-zoom">
                  <Icone nom="zoom_in" taille={14} />
                  Aperçu
                </span>
              </button>
              <div className="pj-corps">
                <strong title={p.nomFichier ?? undefined}>
                  {p.nomFichier ?? LIBELLES_TYPE_PIECE[p.type]}
                </strong>
                <span className="discret">
                  {[
                    LIBELLES_TYPE_PIECE[p.type],
                    p.numero,
                    p.dateDocument ? formaterJour(p.dateDocument) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {note && (
                  <span className="pj-note">
                    <Icone nom="auto_awesome" taille={15} />
                    {note}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <ZoneDepotTache />
      {ouverte && vue !== null && (
        <div className="pj-apercu">
          <div className="pj-apercu-document">
            <div className="pj-apercu-barre">
              <span className="pj-format statique">{format(ouverte)}</span>
              <span className="mono discret">
                {vue + 1} / {pieces.length}
              </span>
              <button
                type="button"
                className="bouton-icone cadre petit"
                title="Pièce précédente"
                aria-label="Pièce précédente"
                onClick={() => setVue((vue - 1 + pieces.length) % pieces.length)}
              >
                <Icone nom="chevron_left" taille={18} />
              </button>
              <button
                type="button"
                className="bouton-icone cadre petit"
                title="Pièce suivante"
                aria-label="Pièce suivante"
                onClick={() => setVue((vue + 1) % pieces.length)}
              >
                <Icone nom="chevron_right" taille={18} />
              </button>
            </div>
            <ApercuDocument
              key={ouverte.id}
              f={{
                url: fichier(ouverte.id),
                nom: ouverte.nomFichier ?? 'Pièce',
                typeMime: ouverte.typeMime,
              }}
            />
          </div>
          <ActionsPiece
            key={ouverte.id}
            pieceId={ouverte.id}
            note={noteDe(dossier.get(ouverte.id))}
            ecriture={ecriture}
            tacheId={tacheId}
            // Le mail d'où vient la pièce, s'il n'est pas déjà celui de la tâche.
            lienMail={
              ouverte.lienMail && !t.mails.some((m) => m.lien === ouverte.lienMail)
                ? ouverte.lienMail
                : null
            }
            onFermer={() => setVue(null)}
          />
        </div>
      )}
    </div>
  )
}

type Geste = 'rattacher' | 'classer' | null

function ActionsPiece({
  pieceId,
  note,
  ecriture,
  tacheId,
  lienMail,
  onFermer,
}: {
  pieceId: string
  note: string | null
  ecriture: boolean
  tacheId: string
  lienMail: string | null
  onFermer: () => void
}) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites)
  const piece = useQuery(requetes.piece(pieceId))
  const [geste, setGeste] = useState<Geste>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const p = piece.data
  const ecrivables = p ? entitesPourPiece(p, entites.data ?? [], moi) : []

  async function agir(action: () => Promise<unknown>, fait: string) {
    setErreur(null)
    setMessage(null)
    try {
      await action()
      await client.invalidateQueries()
      setMessage(fait)
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }

  const transferer = () =>
    agir(
      () =>
        lire(
          api.brouillons.$post({
            json: corps(brouillonCreation, {
              type: 'courriel',
              tacheId,
              objet: p?.nomFichier ?? 'Pièce jointe',
              corps: 'Bonjour,\n\nVous trouverez la pièce ci-jointe.\n\nBien cordialement.',
              pieceIds: [pieceId],
            }),
          }),
        ),
      'Courriel préparé avec la pièce : à relire et valider dans « Préparer », plus bas.',
    )

  const rejeter = async () => {
    if (!(await confirmer('Rejeter cette pièce (publicité, sans rapport, doublon) ?'))) return
    void agir(
      () =>
        lire(
          api.pieces[':id'].action.$post({ param: { id: pieceId }, json: { action: 'rejeter' } }),
        ),
      'Pièce rejetée.',
    )
  }

  const ligne = (
    icone: NomIcone,
    libelle: string,
    detail: string,
    onClick: (() => void) | null,
    danger = false,
  ) =>
    onClick && (
      <button type="button" className={danger ? 'pj-action danger' : 'pj-action'} onClick={onClick}>
        <Icone nom={icone} taille={18} />
        <span>
          <span>{libelle}</span>
          <span className="discret">{detail}</span>
        </span>
      </button>
    )

  return (
    <aside className="pj-actions" aria-label="Actions sur la pièce">
      <div className="pj-actions-tete">
        <strong>Actions</strong>
        <button
          type="button"
          className="bouton-icone"
          title="Fermer l’aperçu"
          aria-label="Fermer l’aperçu"
          onClick={onFermer}
        >
          <Icone nom="close" taille={19} />
        </button>
      </div>
      <div className="pj-actions-corps">
        {note && (
          <div className="pj-suggestion">
            <Icone nom="auto_awesome" taille={16} />
            <span>{note}</span>
          </div>
        )}
        {message && (
          <div className="pj-fait">
            <Icone nom="check" taille={16} />
            {message}
          </div>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
        {!p ? (
          <p className="discret">{piece.isPending ? 'Chargement…' : 'Pièce introuvable.'}</p>
        ) : (
          <>
            {ligne(
              'domain',
              'Rattacher à l’entité',
              p.entite ?? 'Aucune pour l’instant',
              ecriture && ecrivables.length > 0
                ? () => setGeste(geste === 'rattacher' ? null : 'rattacher')
                : null,
            )}
            {geste === 'rattacher' && (
              <div className="pj-geste">
                <RattacherPiece
                  p={p}
                  ecrivables={ecrivables}
                  onFait={() => {
                    setGeste(null)
                    setMessage('Pièce rattachée.')
                  }}
                />
              </div>
            )}
            {ligne(
              'drive_file_move',
              'Classer dans les pièces',
              'Choisir la rubrique ou le dossier du plan',
              ecriture && p.entiteId
                ? () => setGeste(geste === 'classer' ? null : 'classer')
                : null,
            )}
            {geste === 'classer' && (
              <div className="pj-geste">
                <ClasserPiece p={p} ecriture />
              </div>
            )}
            {ligne(
              'forward_to_inbox',
              'Transférer par courriel',
              'Préparé dans la tâche, envoyé par vous',
              ecriture ? () => void transferer() : null,
            )}
            <a
              className="pj-action"
              href={`${fichier(pieceId)}?telecharger=1`}
              download={p.nomFichier ?? true}
            >
              <Icone nom="download" taille={18} />
              <span>
                <span>Télécharger</span>
                <span className="discret">{p.nomFichier ?? 'Fichier d’origine'}</span>
              </span>
            </a>
            <a className="pj-action" href={fichier(pieceId)} target="_blank" rel="noreferrer">
              <Icone nom="open_in_new" taille={18} />
              <span>
                <span>Ouvrir dans un nouvel onglet</span>
                <span className="discret">Fichier d’origine</span>
              </span>
            </a>
            {lienMail && (
              <a className="pj-action" href={lienMail} target="_blank" rel="noreferrer">
                <Icone nom="mail" taille={18} />
                <span>
                  <span>Ouvrir le mail d’origine</span>
                  <span className="discret">Dans Gmail</span>
                </span>
              </a>
            )}
            <Link to="/pieces/$id" params={{ id: pieceId }} className="pj-action">
              <Icone nom="description" taille={18} />
              <span>
                <span>Ouvrir la fiche de la pièce</span>
                <span className="discret">Lecture, montants, historique</span>
              </span>
            </Link>
            {p.statut !== 'ignoree' &&
              ligne(
                'delete',
                'Rejeter la pièce',
                'Publicité, sans rapport, doublon',
                ecriture ? rejeter : null,
                true,
              )}
          </>
        )}
      </div>
    </aside>
  )
}
